import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// Interfaces
interface SubTrackingStep {
  id: string;
  title: string;
  description: string;
  status: 'completed' | 'current' | 'pending' | 'failed';
  date?: string;
  estimatedTime?: string;
  assignedTo?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  aiAnalysis?: string;
  attachments?: string[];
  notifications?: NotificationItem[];
}

interface MainTrackingStep {
  id: number;
  title: string;
  description: string;
  status: 'completed' | 'current' | 'pending' | 'failed';
  date?: string;
  estimatedTime?: string;
  subSteps: SubTrackingStep[];
  roles: {
    tenant: string[];
    landlord: string[];
    business: string[];
  };
  systemTracking: {
    status: string;
    notifications: string[];
    aiAnalysis: string;
  };
}

interface NotificationItem {
  id: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error';
  timestamp: string;
  read: boolean;
}

interface TrackingProperty {
  id: string;
  name: string;
  address: string;
  image: string;
  currentStep: number;
  steps: MainTrackingStep[];
  createdAt: string;
  updatedAt: string;
}

interface TrackingContextType {
  // State
  properties: TrackingProperty[];
  currentProperty: TrackingProperty | null;
  notifications: NotificationItem[];
  
  // Actions
  addProperty: (property: Omit<TrackingProperty, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateProperty: (propertyId: string, updates: Partial<TrackingProperty>) => void;
  setCurrentProperty: (propertyId: string) => void;
  updateStepStatus: (propertyId: string, stepId: number, status: MainTrackingStep['status']) => void;
  updateSubStepStatus: (propertyId: string, stepId: number, subStepId: string, status: SubTrackingStep['status']) => void;
  addNotification: (notification: Omit<NotificationItem, 'id' | 'timestamp'>) => void;
  markNotificationAsRead: (notificationId: string) => void;
  markAllNotificationsAsRead: () => void;
  
  // AI Functions
  generateAIInsights: (propertyId: string) => Promise<string>;
  predictNextSteps: (propertyId: string) => Promise<string[]>;
  analyzeRisk: (propertyId: string) => Promise<{ level: 'low' | 'medium' | 'high'; message: string }>;
}

const TrackingContext = createContext<TrackingContextType | undefined>(undefined);

export const useTracking = () => {
  const context = useContext(TrackingContext);
  if (!context) {
    throw new Error('useTracking must be used within a TrackingProvider');
  }
  return context;
};

interface TrackingProviderProps {
  children: ReactNode;
}

export const TrackingProvider: React.FC<TrackingProviderProps> = ({ children }) => {
  const [properties, setProperties] = useState<TrackingProperty[]>([]);
  const [currentProperty, setCurrentProperty] = useState<TrackingProperty | null>(null);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  // Generate unique ID
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Add new property
  const addProperty = useCallback((property: Omit<TrackingProperty, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newProperty: TrackingProperty = {
      ...property,
      id: generateId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    setProperties(prev => [...prev, newProperty]);
    setCurrentProperty(newProperty);
    
    // Add notification
    addNotification({
      message: `Đã thêm bất động sản mới: ${property.name}`,
      type: 'success',
      read: false
    });
  }, []);

  // Update property
  const updateProperty = useCallback((propertyId: string, updates: Partial<TrackingProperty>) => {
    setProperties(prev => 
      prev.map(property => 
        property.id === propertyId 
          ? { ...property, ...updates, updatedAt: new Date().toISOString() }
          : property
      )
    );
    
    if (currentProperty?.id === propertyId) {
      setCurrentProperty(prev => prev ? { ...prev, ...updates, updatedAt: new Date().toISOString() } : null);
    }
  }, [currentProperty]);

  // Set current property
  const setCurrentPropertyById = useCallback((propertyId: string) => {
    const property = properties.find(p => p.id === propertyId);
    if (property) {
      setCurrentProperty(property);
    }
  }, [properties]);

  // Update step status
  const updateStepStatus = useCallback((propertyId: string, stepId: number, status: MainTrackingStep['status']) => {
    setProperties(prev => 
      prev.map(property => {
        if (property.id === propertyId) {
              const updatedSteps = property.steps.map(step => 
                step.id === stepId 
                  ? { ...step, status: status as MainTrackingStep['status'], date: status === 'completed' ? new Date().toLocaleString('vi-VN') : step.date }
                  : step
              );
          
          // Update current step
          const newCurrentStep = status === 'completed' ? stepId + 1 : stepId;
          
          return {
            ...property,
            steps: updatedSteps,
            currentStep: newCurrentStep,
            updatedAt: new Date().toISOString()
          };
        }
        return property;
      })
    );
    
    // Add notification
    const stepNames = [
      'Xem nhà', 'Ra quyết định và đàm phán', 'Ký hợp đồng và thanh toán ban đầu',
      'Nhận phòng', 'Quản lý hàng tháng', 'Kiểm tra định kỳ', 'Trả phòng', 'Thanh lý hợp đồng'
    ];
    
    addNotification({
      message: `Bước "${stepNames[stepId - 1] || `Bước ${stepId}`}" đã được cập nhật: ${status}`,
      type: status === 'completed' ? 'success' : 'info',
      read: false
    });
  }, []);

  // Update sub step status
  const updateSubStepStatus = useCallback((propertyId: string, stepId: number, subStepId: string, status: SubTrackingStep['status']) => {
    setProperties(prev => 
      prev.map(property => {
        if (property.id === propertyId) {
              const updatedSteps = property.steps.map(step => {
                if (step.id === stepId) {
                  const updatedSubSteps = step.subSteps.map(subStep => 
                    subStep.id === subStepId 
                      ? { ...subStep, status: status as SubTrackingStep['status'], date: status === 'completed' ? new Date().toLocaleString('vi-VN') : subStep.date }
                      : subStep
                  );
                  
                  // Check if all sub steps are completed
                  const allSubStepsCompleted = updatedSubSteps.every(sub => sub.status === 'completed');
                  const stepStatus = allSubStepsCompleted ? 'completed' : 'current';
                  
                  return {
                    ...step,
                    subSteps: updatedSubSteps,
                    status: stepStatus as MainTrackingStep['status'],
                    date: stepStatus === 'completed' ? new Date().toLocaleString('vi-VN') : step.date
                  };
                }
                return step;
              });
          
          return {
            ...property,
            steps: updatedSteps,
            updatedAt: new Date().toISOString()
          };
        }
        return property;
      })
    );
    
    // Add notification
    addNotification({
      message: `Chi tiết "${subStepId}" đã được cập nhật: ${status}`,
      type: status === 'completed' ? 'success' : 'info',
      read: false
    });
  }, []);

  // Add notification
  const addNotification = useCallback((notification: Omit<NotificationItem, 'id' | 'timestamp'>) => {
    const newNotification: NotificationItem = {
      ...notification,
      id: generateId(),
      timestamp: new Date().toISOString()
    };
    
    setNotifications(prev => [newNotification, ...prev]);
  }, []);

  // Mark notification as read
  const markNotificationAsRead = useCallback((notificationId: string) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, read: true }
          : notification
      )
    );
  }, []);

  // Mark all notifications as read
  const markAllNotificationsAsRead = useCallback(() => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );
  }, []);

  // AI Functions
  const generateAIInsights = useCallback(async (propertyId: string): Promise<string> => {
    // Simulate AI analysis
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const property = properties.find(p => p.id === propertyId);
    if (!property) return 'Không tìm thấy bất động sản';
    
    const completedSteps = property.steps.filter(step => step.status === 'completed').length;
    const totalSteps = property.steps.length;
    const progress = (completedSteps / totalSteps) * 100;
    
    return `AI phân tích: Tiến độ ${progress.toFixed(1)}%. Dự kiến hoàn thành trong ${Math.max(1, totalSteps - completedSteps)} tuần tới.`;
  }, [properties]);

  const predictNextSteps = useCallback(async (propertyId: string): Promise<string[]> => {
    // Simulate AI prediction
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const property = properties.find(p => p.id === propertyId);
    if (!property) return [];
    
    const currentStep = property.steps.find(step => step.status === 'current');
    if (!currentStep) return [];
    
    return [
      `Tiếp theo: ${currentStep.title}`,
      'Cần chuẩn bị tài liệu cần thiết',
      'Lên lịch hẹn với các bên liên quan',
      'Theo dõi tiến độ thường xuyên'
    ];
  }, [properties]);

  const analyzeRisk = useCallback(async (propertyId: string): Promise<{ level: 'low' | 'medium' | 'high'; message: string }> => {
    // Simulate AI risk analysis
    await new Promise(resolve => setTimeout(resolve, 1200));
    
    const property = properties.find(p => p.id === propertyId);
    if (!property) return { level: 'high', message: 'Không tìm thấy thông tin bất động sản' };
    
    const failedSteps = property.steps.filter(step => step.status === 'failed').length;
    const pendingSteps = property.steps.filter(step => step.status === 'pending').length;
    
    if (failedSteps > 0) {
      return { level: 'high', message: `Có ${failedSteps} bước thất bại cần xử lý ngay` };
    } else if (pendingSteps > 3) {
      return { level: 'medium', message: `Có ${pendingSteps} bước đang chờ xử lý` };
    } else {
      return { level: 'low', message: 'Mọi thứ đang diễn ra bình thường' };
    }
  }, [properties]);

  const value: TrackingContextType = {
    properties,
    currentProperty,
    notifications,
    addProperty,
    updateProperty,
    setCurrentProperty: setCurrentPropertyById,
    updateStepStatus,
    updateSubStepStatus,
    addNotification,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    generateAIInsights,
    predictNextSteps,
    analyzeRisk,
  };

  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  );
};

export default TrackingContext;
